<template>
  <section class="parking-complex-editor">
    <header class="parking-complex-editor__header">
      <button
        class="parking-complex-editor__back"
        type="button"
        aria-label="Вернуться к парковочным комплексам"
        @click="$emit('back')"
      >
        <svg class="parking-complex-editor__back-icon" aria-hidden="true">
          <use xlink:href="#icon-arrow-right" />
        </svg>
      </button>
      <h2 class="parking-complex-editor__title">
        {{ editorTitle }}
      </h2>
    </header>

    <div class="parking-complex-editor__content">
      <div class="parking-complex-editor__left-column">
        <div class="parking-complex-editor__form" @click="showWidgetPreview">
          <div class="parking-complex-editor__field">
            <SmartInputText
              :value="complexName"
              caption="Название виджета"
              placeholder="Например: ЖК, улица с номером дома"
              maxlength="20"
              :default-if-empty="''"
              :make-focus="!initialExpandedParkingId"
              size="m"
              @input="updateComplexName"
              @focus="showWidgetPreview"
            />
            <div class="parking-complex-editor__hint">
              До 20 символов
            </div>
          </div>

          <div
            v-click-outside="closeAddParkingMenu"
            class="parking-complex-editor__add-parking-wrapper"
            @keydown.esc.stop="closeAddParkingMenu"
          >
            <CamsButton
              class="parking-complex-editor__add-parking"
              type="button"
              :aria-expanded="String(isAddParkingMenuOpen)"
              @click.stop="handleAddParking"
            >
              <img src="@/assets/img/icons/icon-add.svg" alt="">
              <span>Добавить парковку</span>
            </CamsButton>
            <div v-if="isAddParkingMenuOpen" class="parking-complex-editor__add-parking-menu" @click.stop>
              <button type="button" @click="addParking">Создать новую парковку</button>
              <button type="button" @click="openExistingParkingPicker">Добавить существующую парковку</button>
            </div>
          </div>
        </div>

        <transition-group name="parking-card" tag="div" class="parking-complex-editor__parkings">
          <ParkingEditorCard
            v-for="(parking, index) in parkings"
            :key="parking._key"
            :parking="parking"
            :colors="parkingColors"
            :expanded="expandedParkingKey === parking._key"
            :loading-cameras="isLoadingCameras && previewParkingKey === parking._key"
            @focus="showParkingPreview(parking._key)"
            @toggle="toggleParking(parking._key)"
            @delete="removeParking(index)"
            @update="updateParkingDraft(index, $event)"
            @select-cameras="openCameraPicker(index)"
            @edit-map="openMapMarkup(index)"
          />
        </transition-group>

        <div v-if="parkingComplex || parkings.length" class="parking-complex-editor__save-row">
          <CamsButton
            class="parking-complex-editor__save"
            type="button"
            priority="primary"
            :disabled="!canSave || isSaving"
            @click="saveComplex"
          >
            {{ isSaving ? 'Сохранение...' : 'Сохранить' }}
          </CamsButton>
        </div>
      </div>

      <ParkingComplexPreview
        :name="complexName"
        :mode="previewMode"
        :parking="previewParking"
        :parkings="parkings"
        :camera="previewCamera"
        :show-demo-camera="!parkingComplex"
        @show-widget="showWidgetPreview"
      />
    </div>
  </section>
</template>

<script>
import MeshFrameDialog from "@/components/meshCameras/MeshFrameDialog.vue";
import ParkingComplexPreview from "@/components/pacs/analytics/parkingDetection/ParkingComplexPreview.vue";
import ParkingEditorCard from "@/components/pacs/analytics/parkingDetection/ParkingEditorCard.vue";
import ParkingExistingSelectDialog from "@/components/pacs/analytics/parkingDetection/ParkingExistingSelectDialog.vue";
import ParkingMapMarkupDialog from "@/components/pacs/analytics/parkingDetection/ParkingMapMarkupDialog.vue";
import {
  PARKING_ANALYTICS_CAMERAS_REQUEST,
  PARKING_COLORS,
  PARKING_FIELDS,
} from "@/components/pacs/analytics/parkingDetection/parkingComplexConfig.js";
import {isParkingPolygonValid} from "@/components/pacs/analytics/parkingDetection/parkingPolygon.js";
import {
  ACTION_CREATE_PARKING,
  ACTION_CREATE_PARKING_AREA,
  ACTION_DELETE_PARKINGS,
  ACTION_EDIT_PARKING,
  ACTION_EDIT_PARKING_AREA,
  ACTION_LOAD_PARKING_ANALYTICS_CAMERAS,
  ACTION_LOAD_PARKING_CAMERAS,
  ACTION_LOAD_PARKINGS,
  ACTION_SET_PARKING_AREA_PARKINGS,
} from "@/store/analytics/parkingDetections/index.js";

const PARKING_STORE_NAMESPACE = "analytics/parkingDetections";

let nextDraftId = 1;

const clone = value => JSON.parse(JSON.stringify(value));

const createParkingDraft = (parking = {}) => ({
  _key: parking.id ? `parking-${parking.id}` : `draft-${nextDraftId++}`,
  _isExistingAttachment: Boolean(parking._isExistingAttachment),
  _isDirty: false,
  _savedCameraNumbers: [...(parking.camera_numbers || [])],
  id: parking.id || null,
  name: parking.name || "",
  color: parking.color || PARKING_COLORS[0].value,
  polygon: clone(parking.polygon || []),
  camera_numbers: [...(parking.camera_numbers || [])],
  free_spots_count: Number(parking.free_spots_count || 0),
  total_spots_count: Number(parking.total_spots_count || 0),
});

/**
 * Экран создания и редактирования парковочного комплекса.
 */
export default {
  name: "ParkingComplexEditor",
  components: {
    ParkingComplexPreview,
    ParkingEditorCard,
  },
  props: {
    parkingComplex: {
      type: Object,
      default: null,
    },
    initialExpandedParkingId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    const initialParkings = this.parkingComplex
      ? (this.parkingComplex.parkings || []).map(createParkingDraft)
      : [];
    const initialExpandedParking = this.initialExpandedParkingId === null
      ? null
      : initialParkings.find(
        parking => String(parking.id) === String(this.initialExpandedParkingId)
      );
    const initialExpandedParkingKey = initialExpandedParking
      ? initialExpandedParking._key
      : null;

    return {
      complexName: this.parkingComplex ? this.parkingComplex.name : "",
      parkings: initialParkings,
      removedParkingIds: [],
      isAddParkingMenuOpen: false,
      expandedParkingKey: initialExpandedParkingKey,
      previewMode: initialExpandedParkingKey ? "parking" : "widget",
      previewParkingKey: initialExpandedParkingKey,
      parkingColors: PARKING_COLORS,
      isSaving: false,
      isLoadingCameras: false,
      isLoadingExistingParkings: false,
      cameraDetailsByNumber: {},
    };
  },
  computed: {
    editorTitle() {
      return this.parkingComplex
        ? "Парковочный комплекс"
        : "Создание парковочного комплекса";
    },
    previewParking() {
      return this.parkings.find(parking => parking._key === this.previewParkingKey) || null;
    },
    previewCamera() {
      const parking = this.previewParking
        || this.parkings.find(item => item.camera_numbers.length);
      const cameraNumber = parking && parking.camera_numbers[0];

      return cameraNumber ? this.cameraDetailsByNumber[cameraNumber] || null : null;
    },
    canSave() {
      return Boolean(
        this.complexName.trim()
          && this.parkings.length
          && this.parkings.every(parking => (
            (parking._isExistingAttachment && !parking._isDirty) || this.isParkingComplete(parking)
          ))
      );
    },
  },
  created() {
    const cameraNumbers = this.parkings.flatMap(parking => parking.camera_numbers);
    if (cameraNumbers.length) this.loadCameraDetails(cameraNumbers);
  },
  methods: {
    dispatchParkingAction(action, payload) {
      return this.$store.dispatch(`${PARKING_STORE_NAMESPACE}/${action}`, payload);
    },
    rememberCameraDetails(cameras) {
      this.cameraDetailsByNumber = cameras.reduce((result, camera) => ({
        ...result,
        [camera.camera_number]: camera,
      }), {...this.cameraDetailsByNumber});
    },
    async loadCameraDetails(cameraNumbers) {
      const cameras = await this.dispatchParkingAction(
        ACTION_LOAD_PARKING_CAMERAS,
        {cameraNumbers, includeMarkup: false}
      );
      this.rememberCameraDetails(cameras);
      return cameras;
    },
    updateComplexName(value) {
      this.complexName = value;
      this.showWidgetPreview();
    },
    showWidgetPreview() {
      this.previewMode = "widget";
      this.previewParkingKey = null;
    },
    showParkingPreview(parkingKey) {
      this.previewMode = "parking";
      this.previewParkingKey = parkingKey;
    },
    async handleAddParking() {
      if (this.isSaving || this.isLoadingExistingParkings) return;
      if (this.isAddParkingMenuOpen) {
        this.closeAddParkingMenu();
        return;
      }

      this.isLoadingExistingParkings = true;
      try {
        let page = 1;
        do {
          const response = await this.dispatchParkingAction(ACTION_LOAD_PARKINGS, {
            page,
            page_size: 100,
            fields: PARKING_FIELDS,
          });
          if (this._isDestroyed || this._isBeingDestroyed || this.isSaving) return;

          const excludedIds = new Set(this.parkings.filter(parking => parking.id).map(parking => String(parking.id)));
          const hasExistingParkings = (response.results || []).some(
            parking => !parking.is_deleted && !excludedIds.has(String(parking.id))
          );
          if (hasExistingParkings) {
            this.isAddParkingMenuOpen = true;
            return;
          }
          page = response.page && response.page.next;
        } while (page);

        this.addParking();
      } catch (error) {
        if (!this._isDestroyed && !this._isBeingDestroyed) {
          this.notify(error?.fields?.non_field_errors?.[0] || error?.message || "Не удалось загрузить парковки", "error");
        }
      } finally {
        this.isLoadingExistingParkings = false;
      }
    },
    addParking() {
      if (this.isSaving) return;
      this.closeAddParkingMenu();
      const parking = createParkingDraft();
      this.parkings.push(parking);
      this.expandedParkingKey = parking._key;
      this.showParkingPreview(parking._key);
    },
    closeAddParkingMenu() {
      this.isAddParkingMenuOpen = false;
    },
    openExistingParkingPicker() {
      if (this.isSaving) return;
      this.closeAddParkingMenu();
      this.$camsdals.open(
        ParkingExistingSelectDialog,
        {excludedParkingIds: this.parkings.filter(parking => parking.id).map(parking => parking.id)},
        {dialogTitle: "Добавить существующую парковку"},
        {
          name: "js-click-outside",
          onClose: parking => this.attachExistingParking(parking),
        }
      );
    },
    async attachExistingParking(parking) {
      if (!parking || !parking.id || this.isSaving) return;
      if (this.parkings.some(item => String(item.id) === String(parking.id))) return;

      const draft = createParkingDraft({...parking, _isExistingAttachment: true});
      this.parkings.push(draft);
      this.removedParkingIds = this.removedParkingIds.filter(id => String(id) !== String(parking.id));
      this.expandedParkingKey = draft._key;
      this.showParkingPreview(draft._key);
      try {
        await this.loadCameraDetails(draft.camera_numbers);
      } catch (error) {
        this.notify(error.message || "Не удалось загрузить превью камер парковки", "error");
      }
    },
    toggleParking(parkingKey) {
      const willExpand = this.expandedParkingKey !== parkingKey;
      this.expandedParkingKey = willExpand ? parkingKey : null;

      if (willExpand) this.showParkingPreview(parkingKey);
    },
    updateParkingDraft(index, patch) {
      const parking = this.parkings[index];
      const isChanged = Object.entries(patch).some(([key, value]) => !_.isEqual(parking[key], value));
      const updatedParking = {...parking, ...patch, _isDirty: parking._isDirty || isChanged};
      this.$set(this.parkings, index, updatedParking);
      this.showParkingPreview(updatedParking._key);
    },
    removeParking(index) {
      const [removedParking] = this.parkings.splice(index, 1);
      if (removedParking.id && !removedParking._isExistingAttachment) {
        this.removedParkingIds.push(removedParking.id);
      }

      if (this.expandedParkingKey === removedParking._key) {
        this.expandedParkingKey = null;
      }
      if (this.previewParkingKey === removedParking._key) {
        this.showWidgetPreview();
      }
    },
    async openCameraPicker(index) {
      if (this.isLoadingCameras) return;

      const parking = this.parkings[index];
      this.showParkingPreview(parking._key);
      this.isLoadingCameras = true;

      try {
        const response = await this.dispatchParkingAction(
          ACTION_LOAD_PARKING_ANALYTICS_CAMERAS,
          PARKING_ANALYTICS_CAMERAS_REQUEST
        );
        const occupiedCameraNumbers = new Set(
          this.parkings
            .filter((item, parkingIndex) => parkingIndex !== index)
            .flatMap(item => item.camera_numbers)
            .map(String)
        );
        const analyticsCameraNumbers = (response.results || [])
          .map(item => item.camera_number)
          .filter(Boolean);
        const allowedCameraNumbers = [...new Set([
          ...analyticsCameraNumbers,
          ...parking.camera_numbers,
          ...parking._savedCameraNumbers,
        ].map(String))].filter(cameraNumber => !occupiedCameraNumbers.has(cameraNumber));

        this.$camsdals.open(
          MeshFrameDialog,
          {
            initialSelectedCameraNumbers: parking.camera_numbers,
            allowedCameraNumbers,
            selectedCameraIconOnly: true,
          },
          {dialogTitle: "Камеры на парковке"},
          {
            size: "vuedal-auto-width vuedal-all-height",
            name: "js-click-outside",
            onClose: (cameraNumbersFromDialog) => {
              if (!Array.isArray(cameraNumbersFromDialog)) return;
              this.updateParkingDraft(index, {camera_numbers: cameraNumbersFromDialog});
              this.loadCameraDetails(cameraNumbersFromDialog);
            },
          }
        );
      } catch (error) {
        this.notify(error.message, "error");
      } finally {
        this.isLoadingCameras = false;
      }
    },
    async openMapMarkup(index) {
      const parking = this.parkings[index];
      const selectedColor = this.parkingColors.find(color => color.value === parking.color);
      this.showParkingPreview(parking._key);

      if (parking.camera_numbers.length) {
        this.isLoadingCameras = true;
        try {
          await this.loadCameraDetails(parking.camera_numbers);
        } catch (error) {
          this.notify(error.message || "Не удалось загрузить координаты камер", "error");
        } finally {
          this.isLoadingCameras = false;
        }
      }

      this.$camsdals.open(
        ParkingMapMarkupDialog,
        {
          parkingName: parking.name,
          initialPolygon: parking.polygon,
          initialCenter: this.parkingComplex && this.parkingComplex.bbox_centroid,
          cameras: parking.camera_numbers
            .map(cameraNumber => this.cameraDetailsByNumber[cameraNumber])
            .filter(Boolean),
          color: selectedColor ? selectedColor.hex : this.parkingColors[0].hex,
        },
        {className: "parking-map-markup-dialog"},
        {
          size: "vuedal-auto-width vuedal-all-height",
          name: "js-click-outside parking-map-markup-vuedal",
          onClose: (polygon) => {
            if (!Array.isArray(polygon)) return;
            this.updateParkingDraft(index, {polygon});
          },
        }
      );
    },
    isParkingComplete(parking) {
      return Boolean(
        String(parking.name || "").trim()
          && parking.camera_numbers.length
          && isParkingPolygonValid(parking.polygon)
      );
    },
    parkingPayload(parking) {
      const payload = {
        name: parking.name,
        color: parking.color,
        polygon: parking.polygon,
        camera_numbers: parking.camera_numbers,
        comment: "",
      };

      if (!parking.id && this.parkingComplex) {
        payload.parking_area_ids = [this.parkingComplex.id];
      }
      return payload;
    },
    async saveParkingDraft(parking, index) {
      // Привязка существующей парковки не требует повторного создания или изменения её настроек.
      if (parking._isExistingAttachment && !parking._isDirty) return parking;
      const payload = this.parkingPayload(parking);
      const response = parking.id
        ? await this.dispatchParkingAction(ACTION_EDIT_PARKING, {id: parking.id, ...payload})
        : await this.dispatchParkingAction(ACTION_CREATE_PARKING, payload);
      const savedParking = {
        ...parking,
        ...payload,
        ...response,
        id: parking.id || response.id,
      };
      const parkingDraft = createParkingDraft(savedParking);

      this.$set(this.parkings, index, parkingDraft);
      if (this.previewParkingKey === parking._key) this.previewParkingKey = parkingDraft._key;
      if (this.expandedParkingKey === parking._key) this.expandedParkingKey = parkingDraft._key;
      return savedParking;
    },
    async saveComplex() {
      if (!this.canSave || this.isSaving) return;

      this.isSaving = true;
      try {
        const savedParkings = [];
        for (let index = 0; index < this.parkings.length; index += 1) {
          savedParkings.push(await this.saveParkingDraft(this.parkings[index], index));
        }

        const areaPayload = {
          name: this.complexName,
          parking_ids: savedParkings.map(parking => parking.id),
          comment: this.parkingComplex ? this.parkingComplex.comment || "" : "",
        };
        let savedArea;

        if (this.parkingComplex) {
          const response = await this.dispatchParkingAction(ACTION_EDIT_PARKING_AREA, {
            id: this.parkingComplex.id,
            name: areaPayload.name,
            comment: areaPayload.comment,
          });
          await this.dispatchParkingAction(ACTION_SET_PARKING_AREA_PARKINGS, {
            parkingAreaId: this.parkingComplex.id,
            parkingIds: areaPayload.parking_ids,
            comment: areaPayload.comment,
          });
          savedArea = {...this.parkingComplex, ...areaPayload, ...response};
        } else {
          const response = await this.dispatchParkingAction(
            ACTION_CREATE_PARKING_AREA,
            areaPayload
          );
          savedArea = {...areaPayload, ...response};
        }

        if (this.removedParkingIds.length) {
          await this.dispatchParkingAction(ACTION_DELETE_PARKINGS, {
            ids: [...new Set(this.removedParkingIds)],
            comment: "",
          });
        }

        this.removedParkingIds = [];
        this.notify("Парковочный комплекс сохранён", "success");
        this.$emit("saved", savedArea);
      } catch (error) {
        const errorMessage = error?.fields?.non_field_errors?.[0]
          || error?.message
          || "Не удалось сохранить парковочный комплекс";
        this.notify(errorMessage, "error");
      } finally {
        this.isSaving = false;
      }
    },
    notify(text, type) {
      this.$notify({group: "main", text, duration: 4000, type});
    },
  },
};
</script>
